import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from '@pkg/assets/icons/v2/close.svg';
import { CircleButton, YatScrim } from '@pkg/components';

import { useLockBodyScroll } from '@app/helpers';

import './ModalSideSheet.scss';

export enum YatModalSideSheetVariant {
    light = 'light',
    dark = 'dark',
    gradient = 'gradient',
    roundedGradient = 'roundedGradient',
    roundedGradientSmall = 'roundedGradientSmall',
}

export enum YatModalSideSheetSide {
    left = 'left',
    right = 'right',
}

export interface YatModalSideSheetProps {
    children: ReactNode;
    modalLabelId: string;
    open: boolean;
    backdrop?: boolean;
    scrollable?: boolean;
    side?: YatModalSideSheetSide | string;
    variant?: YatModalSideSheetVariant | string;
    classes?: string;
    header?: React.ReactElement;
    closeCallback: () => void;
}

export function YatModalSideSheet({
    children,
    modalLabelId,
    open,
    backdrop = true,
    scrollable = true,
    side = YatModalSideSheetSide.left,
    variant = YatModalSideSheetVariant.dark,
    classes,
    header,
    closeCallback,
}: YatModalSideSheetProps) {
    const { t } = useTranslation(['yat_picker']);
    const containerElement = useRef() as React.MutableRefObject<HTMLDivElement>;
    const sheetElement = useRef() as React.MutableRefObject<HTMLDivElement>;
    const [focusableChilds, setFocusableChilds] = useState<NodeListOf<Element>>();
    const [focusedElementBeforeOpen, setFocusedElementBeforeOpen] = useState<HTMLElement>();
    useLockBodyScroll(open);

    const handleKeyDown = useCallback(
        (e) => {
            if (!open) return;

            // Close modal on ESC
            if (e.key === 'Escape') {
                e.preventDefault();
                closeCallback();
            }

            // Trap focus inside modal
            if (e.key === 'Tab') {
                // if there's only one focusable element just prevent default
                if (focusableChilds?.length === 1) {
                    e.preventDefault();
                }

                // Go to last focusableElement
                if (e.shiftKey && focusableChilds && document.activeElement === focusableChilds[0]) {
                    e.preventDefault();
                    (focusableChilds[focusableChilds.length - 1] as HTMLElement)?.focus();
                }

                // Go to first focusableElement
                if (
                    !e.shiftKey &&
                    focusableChilds &&
                    document.activeElement === focusableChilds[focusableChilds.length - 1]
                ) {
                    e.preventDefault();
                    (focusableChilds[0] as HTMLElement)?.focus();
                }
            }
        },
        [closeCallback, open, focusableChilds],
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    useEffect(() => {
        // Get focused element before modal opens
        if (open) {
            setFocusedElementBeforeOpen(document.activeElement as HTMLElement);
        }

        // Trigger a change in the body's cursor, to make sure mouse cursor in Chrome doesn't stay as
        // a "pointer" until a mousemove happens after clicking an "open modal" button.
        document.body.style.cursor = 'default';
        setTimeout(() => (document.body.style.cursor = ''), 10);
    }, [open]);

    // Get all focusable elements inside modal
    useEffect(() => {
        if (open) {
            const focusableElements =
                'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]):not(.yat-circle-button), [tabindex="0"]';
            setFocusableChilds(sheetElement.current.querySelectorAll(focusableElements));
            //todo, remember to mention - removed 'children' as a dep because it messed with the focus every time a code digit was changed, does it affect anything?
        }
    }, [open, sheetElement]);

    // Handle focus on open/close
    useEffect(() => {
        if (focusableChilds && open) {
            (focusableChilds[0] as HTMLElement)?.focus(); // if opened, set focus to first focusable child
        } else {
            focusedElementBeforeOpen?.focus(); // if closed, return the focus to last focused element outside modal
        }
    }, [open, sheetElement, focusableChilds, focusedElementBeforeOpen]);

    const classNames = cx(
        'yat-modal-side-sheet',
        `yat-modal-side-sheet--${variant}`,
        `yat-modal-side-sheet--${side}`,
        {
            'yat-modal-side-sheet--is-open': open,
            'yat-modal-side-sheet--has-status':
                header?.props?.classes && header?.props?.classes?.some((c) => c.includes('metamask-status')),
        },
        classes,
    );

    return (
        <div
            role="dialog"
            aria-modal={open ? 'true' : undefined}
            aria-hidden={open ? undefined : 'true'}
            aria-labelledby={modalLabelId}
            ref={containerElement}
            className={classNames}
            tabIndex={-1}
        >
            {backdrop && <YatScrim onClick={() => closeCallback()} />}
            <div ref={sheetElement} className="yat-modal-side-sheet__sheet">
                <div className="yat-modal-side-sheet__top-bar">
                    {header || null}
                    <CircleButton
                        classes="yat-modal-side-sheet__close"
                        ariaLabel={t('close_cart')}
                        onClick={() => closeCallback()}
                    >
                        <CloseIcon />
                    </CircleButton>
                </div>
                <div
                    className={cx('yat-modal-side-sheet__content', {
                        'yat-modal-side-sheet__content--scrollable': scrollable,
                    })}
                >
                    {children}
                </div>
            </div>
        </div>
    );
}
